import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFavorites } from '../hooks/useFavorites';
import './FavoriteButton.css';

interface FavoriteButtonProps {
  itemId: string;
  itemType?: 'event' | 'camp' | 'venue' | 'attraction' | 'class' | 'school_district';
  size?: 'small' | 'medium' | 'large';
}

const FavoriteButton: React.FC<FavoriteButtonProps> = ({
  itemId,
  itemType = 'event',
  size = 'medium'
}) => {
  const { user, signInWithGoogle } = useAuth();
  const { isFavorited, toggleFavorite } = useFavorites();
  const [isAnimating, setIsAnimating] = useState(false);
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);

  const favorited = user ? isFavorited(itemId) : false;

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // If user is not authenticated, show sign-in prompt
    if (!user) {
      setShowSignInPrompt(true);
      return;
    }

    // User is authenticated, toggle favorite
    setIsAnimating(true);
    await toggleFavorite(itemId, itemType);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handleSignIn = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Sign in error:', error);
      alert('Failed to sign in. Please try again.');
    }
  };

  const handleClosePrompt = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowSignInPrompt(false);
  };

  return (
    <>
      <button
        className={`favorite-button ${size} ${favorited ? 'favorited' : ''} ${isAnimating ? 'animating' : ''}`}
        onClick={handleClick}
        aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
        title={favorited ? 'Remove from favorites' : 'Add to favorites'}
      >
        {favorited ? '❤️' : '♡'}
      </button>

      {showSignInPrompt && (
        <div className="sign-in-modal-overlay" onClick={handleClosePrompt}>
          <div className="sign-in-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={handleClosePrompt}>×</button>
            <div className="modal-icon">❤️</div>
            <h3>Sign In to Save Favorites</h3>
            <p>Create a free account to save your favorite events and venues!</p>
            <button className="sign-in-button" onClick={handleSignIn}>
              <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Sign in with Google
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default FavoriteButton;
